import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import masterDataService from "../../../../services/masterDataService";
import machineService from "../../../../services/machineService";
import plantService from "../../../../services/plantService";
import userService from "../../../../services/userService";
import pmMatrixService from "../../../../services/pmMatrixService";
import { authService } from "../../../../services";

import PmMatrixFilters from "./PmMatrixFilters";
import PmMatrixTable from "./PmMatrixTable";
import PmTaskDetailsPanel from "./PmTaskDetailsPanel";
import PmTaskAssignmentPanel from "./PmTaskAssignmentPanel";
import PageLoading from "../../../../components/common/PageLoading";
import "./PmMatrixPlannerPage.css";

const PmMatrixPlannerPage = () => {
  const [loading, setLoading] = useState(true);
  const [matrixData, setMatrixData] = useState([]);

  const [plants, setPlants] = useState([]);
  const [assets, setAssets] = useState([]);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [skills, setSkills] = useState([]);
  const [teams, setTeams] = useState([]);

  const [filters, setFilters] = useState({
    plantId: "",
    assetId: "",
    planId: "",
    year: new Date().getFullYear(),
  });

  const [selectedCell, setSelectedCell] = useState(null);
  const [editingTaskResp, setEditingTaskResp] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchMatrixData();
  }, [filters]);

  const fetchInitialData = async () => {
    try {
      const p = await plantService.getAllPlants();
      const m = await machineService.getMachines(tenantId);
      const pl = await masterDataService.getMaintenancePlans(tenantId);
      const u = await userService.getAllUsers().catch(() => []);
      const sk = await masterDataService
        .getTechnicianSkills(tenantId)
        .catch(() => []);
      const tm = await masterDataService
        .getTechnicianTeams(tenantId)
        .catch(() => []);

      setPlants(p || []);
      setAssets(m || []);
      setPlans(pl || []);
      setUsers(u || []);
      setSkills(sk || []);
      setTeams(tm || []);

      // Auto-select plant ONLY for single-plant users
      if (p?.length === 1) {
        setFilters(prev => ({ ...prev, plantId: p[0].plantId.toString() }));
      }
    } catch (err) {
      toast.error("Failed to load filter data");
    }
  };

  const fetchMatrixData = async () => {
    try {
      setLoading(true);
      const data = await pmMatrixService.getMatrixData(
        tenantId,
        filters.plantId ? parseInt(filters.plantId) : null,
        filters.assetId ? parseInt(filters.assetId) : null,
        filters.planId ? parseInt(filters.planId) : null,
        parseInt(filters.year),
      );
      setMatrixData(data || []);
    } catch (err) {
      toast.error("Failed to fetch Matrix Data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };



      // Cascade-reset and helper logic
      if (key === "plantId") {
        next.assetId = "";
        next.planId = "";
      } else if (key === "assetId") {
        next.planId = "";
        // If user picks an asset, and no plant is selected, try to find the plant for that asset
        if (value && !next.plantId) {
          const asset = assets.find((a) => a.machineId === parseInt(value));
          if (asset && asset.plantId) {
            next.plantId = asset.plantId.toString();
          }
        }
      }
      return next;
    });
  };

  const handleCellClick = (plan, task, cellData) => {
    setSelectedCell({ plan, task, cellData });
  };

  const handleRespClick = (plan, task) => {
    setEditingTaskResp({ plan, task });
  };

  const StatusLegend = () => (
    <div className="matrix-legend">
      <span className="legend-title">Dashboard Overview</span>
      <div className="legend-items-group">
        <div className="legend-item">
          <span className="dot dot-completed"></span> Completed
        </div>
        <div className="legend-item">
          <span className="dot dot-scheduled"></span> Scheduled
        </div>
        <div className="legend-item">
          <span className="dot dot-overdue"></span> Overdue
        </div>
        <div className="legend-item">
          <span className="dot dot-empty"></span> Not Scheduled
        </div>
      </div>
    </div>
  );

  return (
    <div className="pm-matrix-page">
      <div className="matrix-page-header">
        <div className="header-info">
          <h1>Planned Maintenance Matrix</h1>
          <p>Production-ready visual planning and compliance tracking</p>
        </div>
        <div
          className="header-actions"
          style={{ display: "flex", gap: "12px" }}
        >
          <button
            className="btn-secondary"
            onClick={() => {
              const month = new Date().toLocaleString("default", {
                month: "short",
              });
              const el = document.querySelector(`.th-month-${month}`);
              const container = document.querySelector(".matrix-table-scroll");
              if (el && container) {
                const scrollPos = el.offsetLeft - container.offsetLeft - 50;
                container.scrollTo({ left: scrollPos, behavior: "smooth" });
              }
            }}
          >
            Jump to Current Month
          </button>
          <button
            className={`btn-refresh ${loading ? "btn-loading" : ""}`}
            onClick={() => {
              fetchInitialData();
              fetchMatrixData();
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span> Updating...
              </>
            ) : (
              <>
                <span className="refresh-icon-text">↻</span> Refresh Matrix
              </>
            )}
          </button>
        </div>
      </div>

      <div className="matrix-content-wrapper">
        <div className="matrix-main">
          <PmMatrixFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            plants={plants}
            assets={assets}
            plans={plans}
          />

          <StatusLegend />

          {loading && matrixData.length === 0 ? (
            <div className="matrix-loading-state">
              <PageLoading message="Fetching production schedule..." />
            </div>
          ) : matrixData.length === 0 ? (
            <div className="matrix-empty-state">
              <div className="empty-icon">📅</div>
              <h3>No maintenance tasks found</h3>
              <p>
                Try adjusting your filters (Plant, Asset, or Plan) to see
                scheduled tasks for {filters.year}.
              </p>
            </div>
          ) : (
            <PmMatrixTable
              matrixData={matrixData}
              onCellClick={handleCellClick}
              onRespClick={handleRespClick}
            />
          )}
        </div>

        {selectedCell && (
          <PmTaskDetailsPanel
            selectedCell={selectedCell}
            selectedYear={parseInt(filters.year)}
            onClose={() => setSelectedCell(null)}
            onUpdate={fetchMatrixData}
            users={users}
            tenantId={tenantId}
          />
        )}

        {editingTaskResp && (
          <PmTaskAssignmentPanel
            editingTaskResp={editingTaskResp}
            onClose={() => setEditingTaskResp(null)}
            onUpdate={fetchMatrixData}
            skills={skills}
            teams={teams}
            tenantId={tenantId}
            selectedAssetId={filters.assetId}
          />
        )}
      </div>
    </div>
  );
};

export default PmMatrixPlannerPage;
